import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTimeoutHint, setShowTimeoutHint] = useState(false);
  const [restoringSession, setRestoringSession] = useState(false);
  const tokenHandoffAttempted = useRef(false);

  // Handle session token handoff from Add-in "Open in Browser"
  useEffect(() => {
    if (tokenHandoffAttempted.current) return;
    
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('token');
    const refreshToken = params.get('refresh_token');
    
    if (accessToken && refreshToken) {
      tokenHandoffAttempted.current = true;
      setRestoringSession(true);
      
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(({ error }) => {
        // Remove tokens from URL immediately for security
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('token');
        cleanUrl.searchParams.delete('refresh_token');
        window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
        
        if (error) {
          console.error('Session restore failed:', error.message);
        }
        setRestoringSession(false);
      });
    }
  }, []);

  // Show timeout hint after 5 seconds of loading
  useEffect(() => {
    if (loading || restoringSession) {
      const timer = setTimeout(() => {
        setShowTimeoutHint(true);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTimeoutHint(false);
    }
  }, [loading, restoringSession]);

  if (loading || restoringSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {restoringSession ? 'Restoring session...' : 'Checking authentication...'}
        </p>
        
        {showTimeoutHint && (
          <div className="mt-4 p-4 bg-muted rounded-lg max-w-md text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Taking longer than expected? Try refreshing the page.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Page
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (!user) {
    // For /addin route, let AddInWrapper handle dialog authentication
    if (location.pathname === '/addin') {
      return <>{children}</>;
    }
    
    // Check if URL still has token params (shouldn't happen, but safety net)
    const params = new URLSearchParams(window.location.search);
    if (params.has('token') && params.has('refresh_token')) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    // For other routes, redirect to /auth
    const redirectParam = encodeURIComponent(location.pathname);
    return <Navigate to={`/auth?redirect=${redirectParam}`} replace />;
  }

  return <>{children}</>;
};